import { useState } from "react";
import { toast } from "sonner";
import SectionHeading from "@/components/SectionHeading";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import SEO from "@/components/SEO";

export default function Contact() {
  const { content } = useContent();
  const S = content.site, C = content.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent. Our team will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch {
      toast.error("Could not submit enquiry. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const mapsSrc = `https://www.google.com/maps?q=${encodeURIComponent(S.maps_query)}&output=embed`;

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <SEO path="/contact" title="Contact · Vadapalani & OMR Chennai"
        description="Contact Victory Aluminium & Hardwares — Vadapalani (Opp. Metro Pillar 133) and OMR (Opposite Sathyabama University) outlets in Chennai. Call +91 91764 31493 for aluminium, ACP and hardware enquiries." />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 mb-14">
        <SectionHeading eyebrow={C.eyebrow} title={C.title} sub={C.sub} />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-6">
          {(content.outlets || []).map((o) => (
            <div key={o.name} className="border border-[#E5E7EB] bg-white p-6">
              <div className="tracking-eyebrow text-[#D4AF37]">Outlet</div>
              <div className="font-display text-lg mt-1">{o.name}</div>
              <div className="text-sm text-[#4B5563] mt-3 flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[#D4AF37] shrink-0" /><span>{o.address}</span></div>
              <a href={`tel:${o.phone}`} className="text-sm text-[#1A1A1A] mt-3 flex items-center gap-3 hover:text-[#D4AF37]"><Phone className="w-4 h-4 text-[#D4AF37]" />{o.phone}</a>
            </div>
          ))}
          <a href={`mailto:${S.email}`} className="block border border-[#E5E7EB] bg-white p-6 hover:border-[#D4AF37] transition-colors">
            <Mail className="w-6 h-6 text-[#D4AF37] mb-3" />
            <div className="tracking-eyebrow text-[#4B5563]">Email</div>
            <div className="font-display text-lg mt-1">{S.email}</div>
            <div className="text-xs text-[#6B7280] mt-1">Alt: {S.phone_alt}</div>
          </a>
          <div className="border border-[#E5E7EB] bg-white">
            <iframe title="Victory Aluminium Location" src={mapsSrc} className="w-full h-72" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 bg-white border border-[#E5E7EB] p-8 md:p-10 h-fit">
          <h3 className="font-display text-2xl md:text-3xl mb-8">Send an enquiry</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name *" value={form.name} onChange={update("name")} testid="contact-input-name" />
            <Field label="Phone *" value={form.phone} onChange={update("phone")} testid="contact-input-phone" />
            <Field label="Email *" type="email" value={form.email} onChange={update("email")} testid="contact-input-email" />
            <Field label="Company / Project" value={form.company} onChange={update("company")} testid="contact-input-company" />
          </div>
          <div className="mt-5">
            <Field label="Subject" value={form.subject} onChange={update("subject")} testid="contact-input-subject" />
          </div>
          <div className="mt-5">
            <label className="block tracking-eyebrow text-[#4B5563] mb-2">Message *</label>
            <textarea data-testid="contact-input-message" rows={5} value={form.message} onChange={update("message")}
              className="w-full bg-transparent border-b-2 border-[#E5E7EB] focus:border-[#D4AF37] outline-none py-3 text-base resize-none transition-colors"
              placeholder="Tell us about your requirement, project size, timeline..." />
          </div>
          <button type="submit" disabled={loading} data-testid="contact-submit-button"
            className="mt-8 btn-charcoal inline-flex items-center gap-2 px-8 py-4 text-xs tracking-widest uppercase font-semibold disabled:opacity-60">
            {loading ? "Sending..." : <>Send Enquiry <Send className="w-4 h-4" /></>}
          </button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block tracking-eyebrow text-[#4B5563] mb-2">{label}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid}
        className="w-full bg-transparent border-b-2 border-[#E5E7EB] focus:border-[#D4AF37] outline-none py-3 text-base transition-colors" />
    </div>
  );
}
